/**
 * Renders one ViewSpec against the LIVE data layer — the heart of the AI-native surface.
 * An agent-generated or pinned view goes through exactly this component, whether it sits in
 * the chat's views rail or the pinned dashboard, so the two never diverge.
 */

import type { ViewRecord } from "@/lib/api";
import { useSite, useSkus } from "@/lib/data";

import { BarChart } from "./bar-view";
import { CapacityChart } from "./capacity-view";
import { DistributionChart } from "./distribution-view";
import { ViewErrorBoundary } from "./error-boundary";
import { FieldChart } from "./field-view";
import { FlowChart } from "./flow-view";
import { LineChart } from "./line-view";
import { MapChart } from "./map-view";
import { NtsChart } from "./nts-view";
import { TableChart } from "./table-view";
import { type ViewChrome, ViewShell, type ViewSize } from "./view-shell";

export type { ViewChrome, ViewSize } from "./view-shell";

interface SpecViewProps {
  spec: ViewRecord["spec"];
  size: ViewSize;
  /** A stable identity for the error boundary's reset key — pass the owning `ViewRecord.id`
   *  when known (a pin, a rail card); falls back to the spec's own JSON when the caller has no
   *  persisted id yet (e.g. a live-streamed `view` event before `done` assigns one). */
  id?: string;
  /** `"bare"` shows only the title and the chart — see `ViewShell`. The pin board uses this on
   *  the card face and surfaces everything else via a click-to-open popup instead. */
  chrome?: ViewChrome;
}

export function SpecView({ spec, size, id, chrome = "full" }: SpecViewProps) {
  // Called unconditionally regardless of template — `capacity` doesn't read the result, but
  // the Rules of Hooks forbid skipping a hook on any render path this component can take, and
  // both queries are `staleTime: Infinity` and shared with every other screen, so the extra
  // call costs nothing once the snapshot is already cached.
  const skusQuery = useSkus();
  const siteQuery = useSite();

  // A spec is agent-authored input, not trusted code — one bad shape must render an inline
  // notice, not blank the rail/dashboard around it. Keyed on the caller's id when it has one
  // (a persisted pin/rail card) so a genuinely new spec always gets a fresh render attempt.
  const resetKey = id ?? JSON.stringify(spec);

  if (spec.template === "capacity") {
    return (
      <ViewShell spec={spec} size={size} chrome={chrome} chartSizing="measured">
        <ViewErrorBoundary resetKey={resetKey}>
          <CapacityChart spec={spec} size={size} chrome={chrome} />
        </ViewErrorBoundary>
      </ViewShell>
    );
  }

  if (skusQuery.error) {
    return (
      <ViewShell spec={spec} size={size} chrome={chrome}>
        <p className="text-destructive text-xs">Could not load the SKU snapshot for this view.</p>
      </ViewShell>
    );
  }
  if (!skusQuery.data) {
    return (
      <ViewShell spec={spec} size={size} chrome={chrome}>
        <p className="text-muted-foreground text-xs">Loading…</p>
      </ViewShell>
    );
  }
  const skus = skusQuery.data;

  switch (spec.template) {
    case "field":
      return (
        <ViewShell spec={spec} size={size} chrome={chrome} chartSizing="measured">
          <ViewErrorBoundary resetKey={resetKey}>
            <FieldChart spec={spec} size={size} skus={skus} chrome={chrome} />
          </ViewErrorBoundary>
        </ViewShell>
      );
    case "bar":
      return (
        <ViewShell spec={spec} size={size} chrome={chrome}>
          <ViewErrorBoundary resetKey={resetKey}>
            <BarChart spec={spec} size={size} skus={skus} chrome={chrome} />
          </ViewErrorBoundary>
        </ViewShell>
      );
    case "distribution":
      return (
        <ViewShell spec={spec} size={size} chrome={chrome}>
          <ViewErrorBoundary resetKey={resetKey}>
            <DistributionChart spec={spec} size={size} skus={skus} chrome={chrome} />
          </ViewErrorBoundary>
        </ViewShell>
      );
    case "table":
      return (
        <ViewShell spec={spec} size={size} chrome={chrome}>
          <ViewErrorBoundary resetKey={resetKey}>
            <TableChart
              spec={spec}
              size={size}
              skus={skus}
              currency={siteQuery.data?.ntsCurrency}
              chrome={chrome}
            />
          </ViewErrorBoundary>
        </ViewShell>
      );
    case "line":
      return (
        <ViewShell spec={spec} size={size} chrome={chrome} chartSizing="measured">
          <ViewErrorBoundary resetKey={resetKey}>
            <LineChart spec={spec} size={size} chrome={chrome} />
          </ViewErrorBoundary>
        </ViewShell>
      );
    case "flow":
      return (
        <ViewShell spec={spec} size={size} chrome={chrome} chartSizing="measured">
          <ViewErrorBoundary resetKey={resetKey}>
            <FlowChart spec={spec} size={size} skus={skus} chrome={chrome} />
          </ViewErrorBoundary>
        </ViewShell>
      );
    case "map":
      return (
        <ViewShell spec={spec} size={size} chrome={chrome} chartSizing="measured">
          <ViewErrorBoundary resetKey={resetKey}>
            <MapChart spec={spec} size={size} skus={skus} chrome={chrome} />
          </ViewErrorBoundary>
        </ViewShell>
      );
    case "nts":
      return (
        <ViewShell spec={spec} size={size} chrome={chrome} chartSizing="measured">
          <ViewErrorBoundary resetKey={resetKey}>
            <NtsChart spec={spec} size={size} skus={skus} chrome={chrome} />
          </ViewErrorBoundary>
        </ViewShell>
      );
  }
}
